/*eslint-disable @typescript-eslint/no-explicit-any*/
import React, { ReactNode, useRef, type JSX } from "react";
import { SxProps} from "@mui/system";
import { SwipeEventData, useSwipeable } from "react-swipeable";
import { Box, IconButton, Theme, useTheme } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { LayoutContextActionType, SwipeActionEnum } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";

export type SwipeableProps  = {
    sx?: SxProps<Theme>;
    children: ReactNode;
    enablePullToRefresh?: boolean;
    disableSwipe?: boolean;
    onRefresh?: () => void;
};

const INITIAL_TOP = 48;
const DELTA_Y_TRIGGER = 48;
const DELTA_X_TRIGGER = 64;
const MAX_ROTATION_DEGREES = 180;
const MAX_FONT_SIZE = 1.5;
const MIN_FONT_SIZE = .5;

export default function Swipeable({
    sx,
    children,
    enablePullToRefresh,
    onRefresh,
    disableSwipe
}: SwipeableProps): JSX.Element {

    const [/*contentLayoutState,*/ ,layoutDispatch] = useLayoutContext();       
    const theme = useTheme();

    const top = useRef(INITIAL_TOP);
    const deltaY = useRef(0);
    const rotate = useRef(0);
    const fontSize = useRef(MIN_FONT_SIZE);
    const swipeRef = useRef<HTMLElement>(null);
    const iconRef = useRef<SVGSVGElement>(null);

    const pullRegionHeight = parseFloat(theme.spacing(25));

    function reset() {
        deltaY.current = 0;

        setTimeout(() => {
            if (swipeRef.current) {
                swipeRef.current.style.display = 'none';
                top.current = INITIAL_TOP;
                swipeRef.current.style.top = `${INITIAL_TOP}px`;
                rotate.current = 0;
                fontSize.current = MIN_FONT_SIZE;
            }
        }, 500);

    }
    function refresh() {
        //console.log('PULL TO REFRESH');
        onRefresh && onRefresh();
    }

    function onSwipedLeft(eventData: SwipeEventData) {
        //console.log(`left: ${deltaY.current}`);        

        if (deltaY.current > DELTA_Y_TRIGGER) {
            refresh();

        } else {

            //console.log(`y: ${eventData.deltaY}`)            
            if (Math.abs(eventData.deltaY) < DELTA_Y_TRIGGER && Math.abs(eventData.deltaX) > DELTA_X_TRIGGER) {            
                // NEXT
                layoutDispatch({
                    type: LayoutContextActionType.SwipeChanged,
                    payload: SwipeActionEnum.Left
                });
            }
        }

        reset();
 
    }

    function onSwipedRight(eventData: SwipeEventData) {
        //console.log(`right: ${deltaY.current}`);
        if (deltaY.current > DELTA_Y_TRIGGER) {
            refresh();
        } else {


            //console.log(`y: ${eventData.deltaY}`)
            if (Math.abs(eventData.deltaY) < DELTA_Y_TRIGGER && Math.abs(eventData.deltaX) > DELTA_X_TRIGGER) { 
                // PREVIOUS
                layoutDispatch({
                    type: LayoutContextActionType.SwipeChanged,
                    payload: SwipeActionEnum.Right
                });
            }
        }
    }

    function onSwiping(eventData: SwipeEventData) {
        const [,initialY] = eventData.initial;
        //console.log(`swipping: ${eventData.dir} deltaY: ${eventData.deltaY} current: ${deltaY.current}`);

        if (initialY < pullRegionHeight) {

            //if (eventData.dir === DOWN) {

                const down = eventData.deltaY > deltaY.current;

                if (eventData.deltaY > DELTA_Y_TRIGGER) {

                    deltaY.current = eventData.deltaY;

    
                    if (swipeRef.current) {
                        swipeRef.current.style.display = 'flex';
    
                        if (down) {
                            if (top.current < (INITIAL_TOP + DELTA_Y_TRIGGER)) {
                                ++top.current;

                                if (rotate.current <= MAX_ROTATION_DEGREES) {
                                    rotate.current += 10;
                                }

                                if (fontSize.current <= MAX_FONT_SIZE) {
                                    fontSize.current += .02;
                                }
                            }
                        } else {
                            if (top.current > 0) {
                                --top.current;
                                if (rotate.current > 0) {
                                    rotate.current -= 10;
                                }

                                if (fontSize.current > MIN_FONT_SIZE) {
                                    fontSize.current -= .02;
                                }

                            }
                        }

                        swipeRef.current.style.top = `${top.current}px`;
                        swipeRef.current.style.transform = `rotate(${rotate.current}deg)`;
                    }

                    if (iconRef.current) {
                        iconRef.current.style.fontSize = `${fontSize.current}rem`;                        
                    }
                }
            //}
            //console.log(`fontSize: ${fontSize.current}`)
        }
    }

    function onSwipedDown(eventData: SwipeEventData) {
        if (deltaY.current > DELTA_Y_TRIGGER) {
           refresh();
        }

        reset();
    }

    const handlers = useSwipeable({
        delta: 20,
        onSwipedLeft: onSwipedLeft,
        onSwipedRight: onSwipedRight,
        onSwiping: enablePullToRefresh ? onSwiping : undefined,
        onSwipedDown: enablePullToRefresh ? onSwipedDown : undefined,
        //preventDefaultTouchmoveEvent: true,
        //trackMouse: true
    });

    return (
        <>
            {disableSwipe ? 
                <Box sx={sx}>
                    {children}
                </Box> :

                <Box sx={sx} {...handlers}>
                    {children}
                </Box>
            }

            {enablePullToRefresh && <Box ref={swipeRef} sx={{display: 'none', 
                                position: 'absolute', 
                                top: `${top.current}px`, 
                                left: '0', right: '0', 
                                zIndex: 1300, 
                                flex: '1 1 auto',
                                 justifyContent: 'center'}}>
                <IconButton sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.grey[200],
                    opacity: .7
                }}>
                    <Refresh ref={iconRef} sx={{fontSize: `${fontSize}rem`}}
                    // fontSize='medium' 
                    />
                </IconButton>
            </Box>}
        </>
   );
}